import React, { useEffect, useState, useRef } from "react"
import { connect } from "react-redux"
import {
    Row,
    Col,
    Card,
    CardBody,
    Table,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    Input
} from "reactstrap"
import { setBreadcrumbItems } from "../../../store/actions"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { useNavigate } from "react-router-dom"
import PaymentApi from "../../../Api/paymentApi"
import { Toast } from "primereact/toast"
import { OverlayPanel } from "primereact/overlaypanel"
import { StandardPagination } from "components/Common"
import "./Requests.scss"

const Requests = props => {
    document.title = "Payment Requests | Pixwik - Admin"
    const navigate = useNavigate()
    const accessToken = localStorage.getItem("accessToken")
    const toast = useRef(null)
    const statusFilterRef = useRef(null)

    const [loading, setLoading] = useState(false)
    const [confirmingPayment, setConfirmingPayment] = useState(false)
    const [requestData, setRequestData] = useState([])
    const [filteredRequestData, setFilteredRequestData] = useState([])
    const [searchTerm, setSearchTerm] = useState("")
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalRequest: 0,
        hasNextPage: false,
        hasPrevPage: false
    })
    const [error, setError] = useState(null)
    const [statusFilter, setStatusFilter] = useState("all")

    // Modal states
    const [viewModal, setViewModal] = useState(false)
    const [selectedRequest, setSelectedRequest] = useState(null)
    const [confirmReason, setConfirmReason] = useState("")

    const breadcrumbItems = [
        { label: "Pixwik", link: "/" },
        { label: "Payments", link: "#" },
        { label: "Requests", link: "#" },
    ]

    const handleBreadcrumbClick = link => {
        if (link !== "#") {
            navigate(link)
        }
    }

    useEffect(() => {
        props.setBreadcrumbItems("Payment Requests", breadcrumbItems)
        fetchPaymentRequests()
    }, [])

    // Filter requests based on search term and status
    useEffect(() => {
        let filtered = requestData

        // Filter by status
        if (statusFilter !== "all") {
            filtered = filtered.filter(request => request.status === statusFilter)
        }

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(request =>
                request.user?.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                request.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                request._id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                request.amount?.toString().includes(searchTerm) ||
                request.status?.toLowerCase().includes(searchTerm.toLowerCase())
            )
        }

        setFilteredRequestData(filtered)
    }, [searchTerm, requestData, statusFilter])

    // Fetch payment requests from API
    const fetchPaymentRequests = async (page = 1) => {
        try {
            setLoading(true)
            setError(null)
            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
                params: {
                    page: page,
                    limit: 10
                }
            }

            const response = await PaymentApi.getPaymentRequest(config)

            if (response.data.status) {
                const requests = response.data.data.populatedWithdrawalRequests || []
                setRequestData(requests)
                setFilteredRequestData(requests)
                setPagination(response.data.data.pagination || {
                    currentPage: 1,
                    totalPages: 1,
                    totalRequest: 0,
                    hasNextPage: false,
                    hasPrevPage: false
                })
            }
        } catch (error) {
            setError("Failed to fetch payment requests")
        } finally {
            setLoading(false)
        }
    }

    // Calculate request stats from data
    const getRequestStats = () => {
        const pendingRequests = requestData.filter(request => request.status === "PENDING").length
        const approvedRequests = requestData.filter(request => request.status === "APPROVED").length
        const rejectedRequests = requestData.filter(request => request.status === "REJECTED").length
        const totalAmount = requestData.reduce((sum, request) => sum + request.amount, 0)

        return [
            {
                title: "Pending Requests",
                iconClass: "ion-ios-time",
                color: "warning",
                total: pendingRequests,
                subtitle: "Awaiting Approval"
            },
            {
                title: "Approved Requests",
                iconClass: "ion-ios-checkmark-circle",
                color: "success",
                total: approvedRequests,
                subtitle: "Confirmed"
            },
            {
                title: "Rejected Requests",
                iconClass: "ion-ios-close-circle",
                color: "danger",
                total: rejectedRequests,
                subtitle: "Declined"
            },
            {
                title: "Total Amount",
                iconClass: "ion-ios-cash",
                color: "info",
                total: `₹${totalAmount.toLocaleString()}`,
                subtitle: "All Requests"
            },
        ]
    }

    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING':
                return 'warning'
            case 'APPROVED':
                return 'success'
            case 'REJECTED':
                return 'danger'
            default:
                return 'secondary'
        }
    }

    const handleViewDetails = (request) => {
        setSelectedRequest(request)
        setConfirmReason("")
        setViewModal(true)
    }

    const submitConfirmPayment = async () => {
        if (!confirmReason.trim()) {
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "Please provide a reason for confirming the payment",
                life: 3000,
            })
            return
        }

        try {
            setConfirmingPayment(true)

            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                }
            }

            const response = await PaymentApi.confirmPayment(
                selectedRequest.user.userId,
                selectedRequest.amount,
                confirmReason,
                selectedRequest._id,
                config
            )

            if (response.data.status) {
                // Update the request in local state
                setRequestData(prevData =>
                    prevData.map(request =>
                        request._id === selectedRequest._id
                            ? { ...request, status: "APPROVED" }
                            : request
                    )
                )

                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Payment confirmed successfully",
                    life: 3000,
                })

                setViewModal(false)
                setSelectedRequest(null)
                setConfirmReason("")
            } else {
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: response.data.message || "Failed to confirm payment",
                    life: 3000,
                })
            }
        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: error.response?.data?.message || "Failed to confirm payment",
                life: 4000,
            })
        } finally {
            setConfirmingPayment(false)
        }
    }

    return (
        <>
            <Toast ref={toast} position="top-right" />
            <Row>
                <Col xs="12">
                    <div className="request-header d-flex align-items-center justify-content-between">
                        <CustomBreadCrumb
                            data={breadcrumbItems}
                            pageTitle="Payment Requests"
                            handleClick={handleBreadcrumbClick}
                        />
                    </div>

                    {/* Request Stats Cards */}
                    <Row className="mt-4 mb-4">
                        {getRequestStats().map((stat, key) => (
                            <Col xl={3} sm={6} key={key}>
                                <Card className={`mini-stat bg-${stat.color} request-card`}>
                                    <CardBody className="card-body mini-stat-img d-flex align-items-center gap-3">
                                        <div className="mini-stat-icon">
                                            <i className={`ion ${stat.iconClass}`}></i>
                                        </div>
                                        <div className="text-white flex-grow-1">
                                            <h6 className="text-uppercase font-size-16 text-white mb-2" style={{ minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                                                {stat.title}
                                            </h6>
                                            <h2 className="text-white mb-1" style={{ minHeight: '48px', display: 'flex', alignItems: 'center', fontSize: '1.75rem' }}>
                                                {typeof stat.total === 'number' ? stat.total.toLocaleString() : stat.total}
                                            </h2>
                                            <small className="text-white-50" style={{ minHeight: '20px', display: 'flex', alignItems: 'center' }}>
                                                {stat.subtitle}
                                            </small>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {/* Requests Table */}
                    <Card>
                        <CardBody>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <h4 className="card-title">Payment Withdrawal Requests</h4>
                                <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => fetchPaymentRequests()}
                                    disabled={loading}
                                >
                                    <i className="mdi mdi-refresh me-1"></i>
                                    {loading ? "Loading..." : "Refresh"}
                                </Button>
                            </div>

                            <div className="data-table-container">
                                <div className="table-responsive">
                                    <Table className="table mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Username</th>
                                            <th>Transaction ID</th>
                                            <th>Amount</th>
                                            <th>Currency</th>
                                            <th>
                                                <span className="d-flex align-items-center gap-2 justify-content-center">
                                                    <span>Status</span>
                                                    <span
                                                        onClick={e => statusFilterRef.current.toggle(e)}
                                                        style={{ cursor: "pointer" }}
                                                    >
                                                        <i className="mdi mdi-filter" />
                                                    </span>
                                                </span>
                                                <OverlayPanel ref={statusFilterRef}>
                                                    <Input
                                                        type="select"
                                                        value={statusFilter}
                                                        onChange={e => {
                                                            setStatusFilter(e.target.value)
                                                            statusFilterRef.current.hide()
                                                        }}
                                                        style={{ width: "150px" }}
                                                    >
                                                        <option value="all">All Status</option>
                                                        <option value="PENDING">Pending</option>
                                                        <option value="APPROVED">Approved</option>
                                                        <option value="REJECTED">Rejected</option>
                                                    </Input>
                                                </OverlayPanel>
                                            </th>
                                            <th>Created Date</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="7" className="text-center py-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="sr-only">Loading...</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : filteredRequestData.map((request) => (
                                            <tr key={request._id} className="request-row">
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div className="avatar-sm me-2">
                                                            <div className="avatar-title rounded-circle bg-primary text-white">
                                                                {request.user?.userName?.charAt(0)?.toUpperCase() || 'U'}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <span className="fw-bold">{request.user?.userName || 'N/A'}</span>
                                                            <br />
                                                            <small className="text-muted">{request.user?.userId}</small>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="transaction-id fw-bold">
                                                        {request._id.slice(-8).toUpperCase()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="amount fw-bold text-success">
                                                        ₹{request.amount.toLocaleString()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="currency">
                                                        {request.currency}
                                                    </span>
                                                </td>
                                                <td>
                                                    <Badge color={getStatusBadge(request.status)}>
                                                        {request.status}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <span className="date">
                                                        {new Date(request.createdAt).toLocaleDateString()}
                                                    </span>
                                                </td>
                                                <td>
                                                    <Button
                                                        color="info"
                                                        size="sm"
                                                        onClick={() => handleViewDetails(request)}
                                                        className="view-btn"
                                                    >
                                                        <i className="mdi mdi-eye me-1"></i>
                                                        View
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    </Table>
                                </div>
                            </div>

                            {error && (
                                <div className="text-center py-4">
                                    <div className="text-danger">
                                        <i className="mdi mdi-alert-circle display-4 d-block mb-2"></i>
                                        {error}
                                        <br />
                                        <Button color="primary" size="sm" onClick={() => fetchPaymentRequests()} className="mt-2">
                                            Retry
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {!loading && !error && filteredRequestData.length === 0 && (
                                <div className="text-center py-4">
                                    <div className="text-muted">
                                        <i className="mdi mdi-cash-multiple display-4 d-block mb-2"></i>
                                        No payment requests found
                                    </div>
                                </div>
                            )}

                            {/* Pagination */}
                            {pagination.totalPages > 1 && (
                                <StandardPagination
                                    currentPage={pagination.currentPage}
                                    totalPages={pagination.totalPages}
                                    onPageChange={(page) => fetchPaymentRequests(page)}
                                />
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* View Details Modal */}
            <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="xl">
                <ModalHeader toggle={() => setViewModal(false)}>
                    Payment Request Details
                </ModalHeader>
                <ModalBody style={{ maxHeight: '80vh', overflowY: 'auto', padding: '20px' }}>
                    {selectedRequest && (
                        <div className="request-details">
                            <Row>
                                <Col md={6}>
                                    <div className="detail-section mb-3">
                                        <h6 className="text-muted mb-2">User Information</h6>
                                        <div className="detail-item">
                                            <strong>Name:</strong> {selectedRequest.user?.firstName} {selectedRequest.user?.lastName}
                                        </div>
                                        <div className="detail-item">
                                            <strong>Username:</strong> {selectedRequest.user?.userName}
                                        </div>
                                        <div className="detail-item">
                                            <strong>Email:</strong> {selectedRequest.user?.email}
                                        </div>
                                        <div className="detail-item">
                                            <strong>Phone:</strong> {selectedRequest.user?.phoneNumber}
                                        </div>
                                        <div className="detail-item">
                                            <strong>User Type:</strong> {selectedRequest.user?.userType}
                                        </div>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="detail-section mb-3">
                                        <h6 className="text-muted mb-2">Payment Information</h6>
                                        <div className="detail-item">
                                            <strong>Amount:</strong> ₹{selectedRequest.amount?.toLocaleString()}
                                        </div>
                                        <div className="detail-item">
                                            <strong>Currency:</strong> {selectedRequest.currency}
                                        </div>
                                        <div className="detail-item">
                                            <strong>Type:</strong> {selectedRequest.type}
                                        </div>
                                        <div className="detail-item">
                                            <strong>Status:</strong>
                                            <Badge color={getStatusBadge(selectedRequest.status)} className="ms-2">
                                                {selectedRequest.status}
                                            </Badge>
                                        </div>
                                        <div className="detail-item">
                                            <strong>Created:</strong> {new Date(selectedRequest.createdAt).toLocaleString()}
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {selectedRequest.bankInfo && (
                                <div className="detail-section mb-3">
                                    <h6 className="text-muted mb-2">Bank Information</h6>
                                    <Row>
                                        <Col md={6}>
                                            <div className="detail-item">
                                                <strong>Account Name:</strong> {selectedRequest.bankInfo.bankAccountName}
                                            </div>
                                            <div className="detail-item">
                                                <strong>Account Number:</strong> {selectedRequest.bankInfo.bankAccountNumber}
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="detail-item">
                                                <strong>Bank Name:</strong> {selectedRequest.bankInfo.bankName}
                                            </div>
                                            <div className="detail-item">
                                                <strong>IFSC Code:</strong> {selectedRequest.bankInfo.bankIfsc}
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            )}

                            {/* Confirm Payment Section - Only show for PENDING requests */}
                            {selectedRequest.status === "PENDING" && (
                                <div className="detail-section">
                                    <h6 className="text-muted mb-2">Confirm Payment</h6>
                                    <Form>
                                        <FormGroup>
                                            <Label for="confirmReason">Reason for Confirmation *</Label>
                                            <Input
                                                type="textarea"
                                                id="confirmReason"
                                                value={confirmReason}
                                                onChange={(e) => setConfirmReason(e.target.value)}
                                                placeholder="Enter reason for confirming this payment..."
                                                rows={3}
                                                required
                                            />
                                        </FormGroup>
                                    </Form>
                                </div>
                            )}
                        </div>
                    )}
                </ModalBody>
                <ModalFooter>
                    {selectedRequest?.status === "PENDING" && (
                        <Button
                            color="success"
                            onClick={submitConfirmPayment}
                            disabled={confirmingPayment || !confirmReason.trim()}
                        >
                            {confirmingPayment ? (
                                <>
                                    <div className="spinner-border spinner-border-sm me-1" role="status"></div>
                                    Confirming...
                                </>
                            ) : (
                                <>
                                    <i className="mdi mdi-check me-1"></i>
                                    Confirm Payment
                                </>
                            )}
                        </Button>
                    )}
                    <Button color="secondary" onClick={() => setViewModal(false)} disabled={confirmingPayment}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>


        </>
    )
}

export default connect(null, { setBreadcrumbItems })(Requests)